import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Landmark, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (forgot) {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        setForgot(false);
      } else {
        await login(form.email, form.password);
        nav("/");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(0deg,#fff 0 1px,transparent 1px 48px),repeating-linear-gradient(90deg,#fff 0 1px,transparent 1px 48px)" }} />
        <div className="relative flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-600"><Landmark className="h-5 w-5" /></div>
          <div>
            <div className="font-heading text-lg font-bold">OTMS</div>
            <div className="text-xs text-slate-400">Philippine LGU Ordinance Tracking and Management System</div>
          </div>
        </div>
        <div className="relative max-w-lg">
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-400">Sanggunian Legislative Records</div>
          <h1 className="text-4xl font-semibold leading-tight tracking-tight lg:text-5xl">From filing to effectivity, every measure accounted for.</h1>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            Registry, numbering, versioned drafting, configurable workflow and append-only audit trails for barangays, municipalities, cities and provinces.
          </p>
        </div>
        <div className="relative text-[11px] text-slate-500">Designed with RA 7160, RA 10173, RA 9470 and RA 8792 as a compliance baseline — not a substitute for legal review.</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight">{forgot ? "Reset password" : "Sign in"}</h2>
            <p className="mt-1 text-sm text-slate-500">{forgot ? "We will issue a reset link for your account." : "Use your LGU-issued account."}</p>
          </div>
          <label className="mb-4 block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Email</span>
            <input data-testid="login-email-input" type="email" required className="otms-input h-10" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          {!forgot && (
            <label className="mb-2 block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Password</span>
              <input data-testid="login-password-input" type="password" required className="otms-input h-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </label>
          )}
          {error && <div data-testid="login-error" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
          <Button data-testid="login-submit-button" type="submit" disabled={busy} className="mt-5 h-10 w-full bg-sky-600 hover:bg-sky-700">
            <Lock className="mr-2 h-4 w-4" /> {busy ? "Please wait…" : forgot ? "Request reset link" : "Sign in"}
          </Button>
          <button type="button" data-testid="toggle-forgot-password" onClick={() => setForgot(!forgot)} className="mt-4 text-xs text-sky-700 hover:underline">
            {forgot ? "Back to sign in" : "Forgot password?"}
          </button>
        </form>
      </div>
    </div>
  );
}
